import type { PublicProfile } from '@/types'
import ProfileBranding from '@/components/profile/ProfileBranding'
import { MapPin, Linkedin, Github, Globe, Mail, Phone, Download } from 'lucide-react'

interface ModernTemplateProps {
  profile: PublicProfile
}

export default function ModernTemplate({ profile }: ModernTemplateProps) {
  const { visibility } = profile.profileSettings
  const fullName = `${profile.firstName} ${profile.lastName}`
  const initials = `${profile.firstName.charAt(0)}${profile.lastName.charAt(0)}`.toUpperCase()

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Liquid gradient background */}
      <div className="fixed inset-0 bg-gradient-to-br from-blue-200 via-purple-200 to-pink-200"></div>

      {/* Animated gradient blobs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute top-0 right-0 w-[800px] h-[800px] rounded-full blur-3xl opacity-60 animate-pulse"
          style={{
            background: `radial-gradient(circle, var(--color-primary) 0%, transparent 70%)`,
            animation: 'float 20s ease-in-out infinite',
          }}
        ></div>
        <div
          className="absolute bottom-0 left-0 w-[600px] h-[600px] rounded-full blur-3xl opacity-50"
          style={{
            background: `radial-gradient(circle, var(--color-primary-dark) 0%, transparent 70%)`,
            animation: 'float 15s ease-in-out infinite reverse',
          }}
        ></div>
        <div
          className="absolute top-1/2 left-1/2 w-[500px] h-[500px] rounded-full blur-3xl opacity-45 -translate-x-1/2 -translate-y-1/2"
          style={{
            background: `radial-gradient(circle, var(--color-primary-light) 0%, transparent 70%)`,
            animation: 'float 25s ease-in-out infinite',
          }}
        ></div>
      </div>

      {/* Hero section with glass effect */}
      <div className="relative z-10">
        <div className="container-custom py-20 md:py-32">
          <div
            className={`grid grid-cols-1 ${visibility.showPhoto ? 'lg:grid-cols-12' : ''} gap-12 items-center`}
          >
            {/* Left side - Profile info */}
            <div className={visibility.showPhoto ? 'lg:col-span-7' : ''}>
              <div className="backdrop-blur-2xl bg-white/40 rounded-none p-10 md:p-14 shadow-2xl border border-white/60">
                {profile.title && (
                  <div className="inline-block mb-6">
                    <span
                      className="text-sm font-semibold px-5 py-2.5 rounded-full backdrop-blur-xl bg-white/70 shadow-lg border border-white/80"
                      style={{ color: 'var(--color-primary-dark)' }}
                    >
                      {profile.title}
                    </span>
                  </div>
                )}

                <h1 className="text-6xl md:text-7xl font-bold mb-6 leading-tight bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 bg-clip-text text-transparent">
                  {fullName}
                </h1>

                {profile.location && (
                  <div className="flex items-center gap-2 text-gray-600 mb-6">
                    <MapPin className="w-5 h-5" />
                    <span className="text-lg font-medium">{profile.location}</span>
                  </div>
                )}

                {profile.bio && (
                  <p className="text-xl text-gray-700 mb-8 leading-relaxed font-medium">
                    {profile.bio}
                  </p>
                )}

                {/* Social links */}
                {visibility.showContact && (
                  <div className="flex flex-wrap gap-3">
                    {profile.linkedInUrl && (
                      <a
                        href={profile.linkedInUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-5 py-3 backdrop-blur-xl bg-white/70 rounded-2xl hover:bg-white/90 transition-all shadow-lg border border-white/80 text-gray-700 font-semibold"
                      >
                        <Linkedin className="w-5 h-5" />
                        <span>LinkedIn</span>
                      </a>
                    )}
                    {profile.githubUrl && (
                      <a
                        href={profile.githubUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-5 py-3 backdrop-blur-xl bg-white/70 rounded-2xl hover:bg-white/90 transition-all shadow-lg border border-white/80 text-gray-700 font-semibold"
                      >
                        <Github className="w-5 h-5" />
                        <span>GitHub</span>
                      </a>
                    )}
                    {profile.portfolioUrl && (
                      <a
                        href={profile.portfolioUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-5 py-3 backdrop-blur-xl bg-white/70 rounded-2xl hover:bg-white/90 transition-all shadow-lg border border-white/80 text-gray-700 font-semibold"
                      >
                        <Globe className="w-5 h-5" />
                        <span>Portfolio</span>
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Right side - Profile photo */}
            {visibility.showPhoto && (
              <div className="lg:col-span-5 flex justify-center lg:justify-end">
                <div className="relative">
                  <div className="w-72 h-72 md:w-96 md:h-96 backdrop-blur-2xl bg-white/30 rounded-none rounded-avatar p-2 shadow-2xl border border-white/60">
                    {profile.profilePhoto ? (
                      <img
                        src={profile.profilePhoto}
                        alt={fullName}
                        className="w-full h-full object-cover rounded-none rounded-avatar"
                      />
                    ) : (
                      <div
                        className="w-full h-full rounded-none rounded-avatar flex items-center justify-center text-white text-7xl font-bold shadow-inner"
                        style={{
                          background: `linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 100%)`,
                        }}
                      >
                        {initials}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main content - Floating glass cards */}
      <div className="relative z-10 container-custom pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Experience - Large glass card */}
          {visibility.showExperience && profile.experience && profile.experience.length > 0 && (
            <div className="lg:col-span-8">
              <div className="backdrop-blur-2xl bg-white/40 rounded-none p-8 md:p-10 shadow-2xl border border-white/60">
                <h2 className="text-4xl font-bold mb-8 text-gray-900">Experience</h2>
                <div className="space-y-8">
                  {profile.experience.map((exp, index) => (
                    <div key={index} className="relative pl-8 border-l-4 border-white/40">
                      <div
                        className="absolute left-0 top-0 w-4 h-4 rounded-full -translate-x-[10px] shadow-lg"
                        style={{ backgroundColor: 'var(--color-primary)' }}
                      ></div>
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-xl font-bold text-gray-900">{exp.jobTitle}</h3>
                          {exp.isCurrent && (
                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                              Current
                            </span>
                          )}
                        </div>
                        <span className="text-sm font-semibold px-4 py-1.5 bg-white/60 backdrop-blur-xl rounded-full text-gray-600 whitespace-nowrap border border-white/80">
                          {exp.startDate} - {exp.isCurrent ? 'Present' : exp.endDate}
                        </span>
                      </div>
                      <p
                        className="text-lg font-semibold mb-2"
                        style={{ color: 'var(--color-primary-dark)' }}
                      >
                        {exp.companyName} {exp.location && `• ${exp.location}`}
                      </p>
                      {exp.description && (
                        <p className="text-gray-700 leading-relaxed">{exp.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Right column - stacked glass cards */}
          <div className="lg:col-span-4 space-y-6">
            {/* Contact card */}
            {visibility.showContact && (profile.email || profile.phone) && (
              <div className="backdrop-blur-2xl bg-white/40 rounded-none p-8 shadow-2xl border border-white/60">
                <h3 className="text-2xl font-bold mb-6 text-gray-900">Contact</h3>
                <div className="space-y-4">
                  {profile.email && (
                    <a
                      href={`mailto:${profile.email}`}
                      className="flex items-center gap-3 text-gray-700 hover:text-gray-900 transition-colors group"
                    >
                      <div className="p-2 rounded-xl bg-white/60 backdrop-blur-xl group-hover:bg-white/80 transition-colors border border-white/80">
                        <Mail className="w-5 h-5" />
                      </div>
                      <span className="break-all font-medium">{profile.email}</span>
                    </a>
                  )}
                  {profile.phone && (
                    <a
                      href={`tel:${profile.phone}`}
                      className="flex items-center gap-3 text-gray-700 hover:text-gray-900 transition-colors group"
                    >
                      <div className="p-2 rounded-xl bg-white/60 backdrop-blur-xl group-hover:bg-white/80 transition-colors border border-white/80">
                        <Phone className="w-5 h-5" />
                      </div>
                      <span className="font-medium">{profile.phone}</span>
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Skills card */}
            {visibility.showSkills && profile.skills && profile.skills.length > 0 && (
              <div className="backdrop-blur-2xl bg-white/40 rounded-none p-8 shadow-2xl border border-white/60">
                <h3 className="text-2xl font-bold mb-6 text-gray-900">Skills</h3>
                <div className="flex flex-wrap gap-2">
                  {profile.skills.map((skill, index) => (
                    <span
                      key={index}
                      className="px-4 py-2 rounded-xl text-sm font-semibold backdrop-blur-xl bg-white/70 border border-white/80 shadow-sm"
                      style={{ color: 'var(--color-primary-dark)' }}
                    >
                      {skill.skill?.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Languages card */}
            {visibility.showLanguages && profile.languages && profile.languages.length > 0 && (
              <div className="backdrop-blur-2xl bg-white/40 rounded-none p-8 shadow-2xl border border-white/60">
                <h3 className="text-2xl font-bold mb-6 text-gray-900">Languages</h3>
                <div className="space-y-3">
                  {profile.languages?.map((lang, index) => (
                    <div key={index} className="flex justify-between items-center">
                      <span className="font-semibold text-gray-900">
                        {lang.language?.name ?? ''}
                      </span>
                      <span
                        className="text-sm font-bold px-3 py-1 rounded-lg bg-white/60 backdrop-blur-xl border border-white/80"
                        style={{ color: 'var(--color-primary-dark)' }}
                      >
                        {lang.proficiencyLevel}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Education - Full width glass card */}
          {visibility.showEducation && profile.education && profile.education.length > 0 && (
            <div className="lg:col-span-12">
              <div className="backdrop-blur-2xl bg-white/40 rounded-none p-8 md:p-10 shadow-2xl border border-white/60">
                <h2 className="text-4xl font-bold mb-8 text-gray-900">Education</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {profile.education.map((edu, index) => (
                    <div
                      key={index}
                      className="p-6 rounded-2xl backdrop-blur-xl bg-white/60 border border-white/80 shadow-lg"
                    >
                      <h3 className="text-lg font-bold text-gray-900 mb-1">{edu.degree}</h3>
                      <p
                        className="font-semibold mb-2"
                        style={{ color: 'var(--color-primary-dark)' }}
                      >
                        {edu.institution}
                      </p>
                      {edu.fieldOfStudy && (
                        <p className="text-sm text-gray-600 mb-2">{edu.fieldOfStudy}</p>
                      )}
                      <p className="text-sm font-semibold text-gray-500">
                        {edu.startDate} - {edu.endDate}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Projects - Full width glass card */}
          {visibility.showProjects && profile.projects && profile.projects.length > 0 && (
            <div className="lg:col-span-12">
              <div className="backdrop-blur-2xl bg-white/40 rounded-none p-8 md:p-10 shadow-2xl border border-white/60">
                <h2 className="text-4xl font-bold mb-8 text-gray-900">Projects</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {profile.projects.map((project, index) => (
                    <div
                      key={index}
                      className="p-6 rounded-2xl backdrop-blur-xl bg-white/60 border border-white/80 shadow-lg"
                    >
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <h3 className="text-lg font-bold text-gray-900">{project.title}</h3>
                        {project.isCurrent && (
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                            Current
                          </span>
                        )}
                      </div>
                      {(project.startDate || project.endDate) && (
                        <p className="text-sm font-semibold text-gray-500 mb-3">
                          {project.startDate || 'N/A'} -{' '}
                          {project.isCurrent ? 'Present' : project.endDate || 'N/A'}
                        </p>
                      )}
                      {project.description && (
                        <p className="text-sm text-gray-700 mb-3 line-clamp-3">
                          {project.description}
                        </p>
                      )}
                      {project.technologies && (
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {project.technologies.split(',').map((tech, i) => (
                            <span
                              key={i}
                              className="px-2 py-1 text-xs font-medium rounded-lg bg-white/70 text-gray-700"
                            >
                              {tech.trim()}
                            </span>
                          ))}
                        </div>
                      )}
                      {(project.projectUrl || project.repositoryUrl) && (
                        <div className="flex gap-3 pt-2 border-t border-white/50">
                          {project.projectUrl && (
                            <a
                              href={project.projectUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm font-semibold hover:underline"
                              style={{ color: 'var(--color-primary-dark)' }}
                            >
                              Live Demo →
                            </a>
                          )}
                          {project.repositoryUrl && (
                            <a
                              href={project.repositoryUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm font-semibold hover:underline"
                              style={{ color: 'var(--color-primary-dark)' }}
                            >
                              Source Code →
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Documents */}
          {visibility.showDocuments && profile.cvUrl && (
            <div className="lg:col-span-12">
              <div className="backdrop-blur-2xl bg-white/40 rounded-none p-8 shadow-2xl border border-white/60">
                <a
                  href={profile.cvUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between group"
                >
                  <div>
                    <h3 className="text-2xl font-bold mb-2 text-gray-900">Download CV</h3>
                    <p className="text-gray-600 font-medium">Get my full resume as PDF</p>
                  </div>
                  <div className="p-4 rounded-2xl bg-white/60 backdrop-blur-xl group-hover:bg-white/80 transition-colors border border-white/80">
                    <Download className="w-8 h-8" style={{ color: 'var(--color-primary)' }} />
                  </div>
                </a>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Subtle Branding */}
      <ProfileBranding variant="light" />

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) translateX(0px); }
          50% { transform: translateY(-30px) translateX(20px); }
        }
      `}</style>
    </div>
  )
}
